import { getConfig } from "../config";
import { bffRootForRegion, RegionId } from "../config/regionMap";
import { logger } from "../utility/logger";

/**
 * Runtime region selection for the single-SWA / multi-Function-App model.
 *
 * The app is one static site that talks to whichever region's Function App the current
 * user belongs to. `bffRoot` is therefore chosen at runtime (not baked per build):
 *
 *  - On auth, PrivateRoute resolves the user's home region from their Graph companyName
 *    and calls setActiveRegion() BEFORE the first BFF call.
 *  - CORP users switch region via the org switcher, which calls setActiveRegion() again.
 *
 * `getBffRoot()` is the single seam the API layer (ApiService) and realtime layer
 * (signalRService) read, so both follow the active region automatically.
 *
 * The user's OWN region is tracked separately (setHomeRegion / getHomeBffRoot) because the
 * active one moves when a CORP user is placed in another org, while a handful of calls are
 * about the person and must stay on the region hosting THEIR row — see the self-scoped calls
 * in ApiService (TOS acceptance, support-tenant preference). For a regular user the two are
 * the same; for a corp-capable user both resolve to the default `bffRoot` (their companyName
 * is not an org code, so it maps to no region).
 */

let activeRegion: RegionId | null = null;
let activeBffRoot: string | null = null;
let homeRegion: RegionId | null = null;
let homeBffRoot: string | null = null;

/**
 * Resolve a region's Function App, warning loudly when the region is known to ORG_REGION
 * but absent from this environment's `bffRoots`.
 *
 * The fallback to the default `bffRoot` is deliberate and unchanged (an unmapped or
 * corp-capable user has no region), but for a NAMED region it is a misconfiguration with a
 * nasty signature: the impersonation still applies, so the session ends up asking the
 * DEFAULT region's Function App for `SUPPORT_<ORG>USER`, whose row does not exist there —
 * RLS then returns nothing and the app looks empty rather than broken. This is the first
 * thing to check when a newly onboarded region reads blank: every environment's BffRoots
 * secret needs a key for every region a CORP user can switch into, not just its own.
 */
function resolveRegionRoot(region: RegionId | undefined, slot: string): string | null {
  if (!region) return null;

  const root = bffRootForRegion(region) ?? null;
  if (!root) {
    logger.warn(
      `regionRuntime: no bffRoots entry for region "${region}" (${slot}); falling back to the default Function App. ` +
        "Add that region to this environment's BffRoots secret."
    );
  }
  return root;
}

/** The Function App base URL for the active region, or the configured default. */
export function getBffRoot(): string {
  return activeBffRoot ?? getConfig().bffRoot;
}

/**
 * The Function App base URL for the user's OWN region — never the org they are supporting.
 * Falls back to the default `bffRoot` when their region is unknown (corp-capable users) or
 * unmapped, which is also the pre-multi-region behaviour.
 */
export function getHomeBffRoot(): string {
  return homeBffRoot ?? getConfig().bffRoot;
}

/**
 * Record the region hosting the user's own data. Called once at login (PrivateRoute, from
 * their Graph companyName) and deliberately NOT touched by the org switcher: impersonating
 * another org must not move where the user's own row is read from.
 */
export function setHomeRegion(region: RegionId | undefined): void {
  homeRegion = region ?? null;
  homeBffRoot = resolveRegionRoot(region, "home");
}

/** The user's own region, or null when using the default. */
export function getHomeRegion(): RegionId | null {
  return homeRegion;
}

/** The region currently targeted, or null when using the default. */
export function getActiveRegion(): RegionId | null {
  return activeRegion;
}

/**
 * Point the API + realtime layers at a region's Function App. An unknown/undefined
 * region (e.g. a corp-capable or unmapped user) falls back to the default `bffRoot`.
 * SignalR follows automatically: its consumers stop the hub on unmount and reopen it
 * on the next mount, and startConnection reads getBffRoot() at call time.
 */
export function setActiveRegion(region: RegionId | undefined): void {
  activeRegion = region ?? null;
  activeBffRoot = resolveRegionRoot(region, "active");
}

/** Reset both the active and the home region to the default (e.g. between tests). */
export function resetActiveRegion(): void {
  activeRegion = null;
  activeBffRoot = null;
  homeRegion = null;
  homeBffRoot = null;
}
